import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router";
import { ArrowRight } from "@phosphor-icons/react";
import { BOOKING_URL, signupUrl } from "@/lib/links";

/** The path from here, spelled out. Same three steps on every page. */
const NEXT_STEPS = [
  "Sign up",
  "Run a guided hunt on the sample workspace, no connector needed",
  "Connect your own sources when you're ready",
];

interface ClosingCTAProps {
  heading?: React.ReactNode;
  description?: string;
  primaryLabel?: string;
  primaryHref?: string;
  secondaryLabel?: string;
  secondaryHref?: string;
  /** Hide Scout on pages where the illustration has already appeared twice. */
  scout?: boolean;
  /** Analytics placement for the signup link (lands in utm_content). */
  placement?: string;
}

const isExternal = (href: string) => /^https?:\/\//.test(href);

/** An on-site route through the router, anything absolute as a plain link. */
function CtaLink({
  href,
  className,
  children,
  placement,
}: {
  href: string;
  className: string;
  children: React.ReactNode;
  placement?: string;
}) {
  return isExternal(href) ? (
    <a href={href} className={className} data-cta-placement={placement}>
      {children}
    </a>
  ) : (
    <Link to={href} className={className} data-cta-placement={placement}>
      {children}
    </Link>
  );
}

/**
 * The closing band of the 2026 redesign: one flat violet moment, the headline
 * on the left, Scout running on the right. Every entrance animation is skipped
 * under prefers-reduced-motion.
 */
export function ClosingCTA({
  heading = (
    <>
      Your data.
      <br />
      Your knowledge.
      <br />
      Your call.
    </>
  ),
  description = "Search what you already have, ask the host when it was never collected, and approve every step Scout takes. Sign up and start on sample data today.",
  primaryLabel = "Sign up",
  primaryHref,
  secondaryLabel = "Watch Scout hunt",
  secondaryHref = "/demo",
  scout = true,
  placement = "closing-cta",
}: ClosingCTAProps) {
  const reduce = useReducedMotion();
  const rise = (y: number, delay = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.6, delay },
        };

  return (
    <section
      className="relative overflow-hidden bg-primary-500 text-white py-16 md:py-32"
      aria-labelledby="closing-cta-heading"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-[1.4fr_auto] gap-12 items-center">
        <div>
          <motion.h2
            id="closing-cta-heading"
            {...rise(16)}
            className="heading-xl text-white text-balance"
          >
            {heading}
          </motion.h2>
          <motion.p
            {...rise(12, 0.1)}
            className="mt-6 max-w-xl text-lg text-white leading-relaxed"
          >
            {description}
          </motion.p>
          <motion.div {...rise(12, 0.2)} className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <CtaLink
              href={primaryHref ?? signupUrl(placement)}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-primary-600 shadow-lg hover:bg-primary-200 transition-colors"
            >
              {primaryLabel}
              <ArrowRight
                size={18}
                weight="bold"
                aria-hidden
                className="transition-transform group-hover:translate-x-0.5"
              />
            </CtaLink>
            <CtaLink
              href={secondaryHref}
              placement={placement}
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/60 px-8 py-4 text-base font-semibold text-white hover:bg-white/10 transition-colors"
            >
              {secondaryLabel}
              <ArrowRight
                size={18}
                weight="bold"
                aria-hidden
                className="transition-transform group-hover:translate-x-0.5"
              />
            </CtaLink>
            <Link
              to={BOOKING_URL}
              className="text-sm font-semibold text-white underline underline-offset-4 decoration-white/60 hover:decoration-white sm:ml-3"
            >
              Or book a demo
            </Link>
          </motion.div>
          <motion.ol
            {...(reduce
              ? {}
              : {
                  initial: { opacity: 0 },
                  whileInView: { opacity: 1 },
                  viewport: { once: true },
                  transition: { duration: 0.6, delay: 0.3 },
                })}
            className="mt-10 hidden flex-col gap-2 border-t border-white/30 pt-6 font-mono sm:flex text-xs tracking-[0.04em] text-white sm:flex-row sm:gap-8"
            aria-label="What happens next"
          >
            {NEXT_STEPS.map((step, i) => (
              <li key={step} className="flex items-baseline gap-2.5">
                <span className="text-white/90">0{i + 1}</span>
                {step}
              </li>
            ))}
          </motion.ol>
        </div>
        {scout && (
          <motion.img
            src="/assets/scout/scout-running.webp"
            alt=""
            aria-hidden
            width={260}
            height={260}
            loading="lazy"
            {...(reduce
              ? {}
              : {
                  initial: { opacity: 0, x: 32, rotate: 4 },
                  whileInView: { opacity: 1, x: 0, rotate: 0 },
                  viewport: { once: true, margin: "-120px" },
                  transition: { type: "spring" as const, stiffness: 110, damping: 16 },
                  whileHover: { rotate: -3 },
                })}
            className="hidden lg:block w-60 h-60 drop-shadow-[0_20px_30px_rgba(0,0,0,0.3)]"
          />
        )}
      </div>
    </section>
  );
}
